import { useState } from 'react';
import { defaultConfluences } from '../utils/constants';

/**
 * Custom hook for confluence CRUD operations.
 * Extracted from TradingJournal.js (Phase 4).
 *
 * Manages: confluence editing, deletion, category rename, bulk copy.
 * Owns editing state (editingConfluence, editConfluenceName, editConfluenceCategory).
 */
const useConfluenceManagement = ({
  setCustomConfluences,
  confluenceOverrides, setConfluenceOverrides,
  confluences,
  trades, setTrades,
  setSetupDetails
}) => {
  // Editing state (owned by this hook — only used by confluence editing UI)
  const [editingConfluence, setEditingConfluence] = useState(null);
  const [editConfluenceName, setEditConfluenceName] = useState('');
  const [editConfluenceCategory, setEditConfluenceCategory] = useState('');

  const startEditingConfluence = (confluence) => {
    setEditingConfluence(confluence.id);
    setEditConfluenceName(confluence.name);
    setEditConfluenceCategory(confluence.category);
  };

  const saveEditedConfluence = () => {
    if (!editConfluenceName.trim() || !editingConfluence) {
      cancelEditingConfluence();
      return;
    }
    const newName = editConfluenceName.trim();
    const newCategory = editConfluenceCategory.trim();
    const isCustom = editingConfluence.startsWith('custom-');

    if (isCustom) {
      setCustomConfluences(prev => prev.map(c =>
        c.id === editingConfluence ? { ...c, name: newName, category: newCategory } : c
      ));
    } else {
      const original = defaultConfluences.find(dc => dc.id === editingConfluence);
      if (original) {
        const nameChanged = newName !== original.name;
        const categoryChanged = newCategory !== original.category;
        if (nameChanged || categoryChanged) {
          setConfluenceOverrides(prev => ({
            ...prev,
            [editingConfluence]: {
              ...(nameChanged ? { name: newName } : {}),
              ...(categoryChanged ? { category: newCategory } : {})
            }
          }));
        } else {
          // Reverted to original — remove override
          setConfluenceOverrides(prev => {
            const next = { ...prev };
            delete next[editingConfluence];
            return next;
          });
        }
      }
    }
    cancelEditingConfluence();
  };

  const cancelEditingConfluence = () => {
    setEditingConfluence(null);
    setEditConfluenceName('');
    setEditConfluenceCategory('');
  };

  const deleteConfluence = (confluenceId) => {
    const isCustom = confluenceId.startsWith('custom-');
    const conf = confluences.find(c => c.id === confluenceId);

    // Count trades using this confluence
    const affectedTrades = trades.filter(t => (t.confluences || []).includes(confluenceId));
    const tradeCount = affectedTrades.length;

    const msg = tradeCount > 0
      ? `Delete "${conf?.name || confluenceId}"? It is used by ${tradeCount} trade${tradeCount > 1 ? 's' : ''} — it will be removed from those trades.`
      : `Delete "${conf?.name || confluenceId}"?`;

    if (!window.confirm(msg)) { document.body.focus(); return; }
    document.body.focus();

    if (isCustom) {
      setCustomConfluences(prev => prev.filter(c => c.id !== confluenceId));
    } else {
      setConfluenceOverrides(prev => ({
        ...prev,
        [confluenceId]: { ...(prev[confluenceId] || {}), deleted: true }
      }));
    }

    // Clean up trade.confluences references
    if (tradeCount > 0) {
      setTrades(prev => prev.map(t => {
        if (!(t.confluences || []).includes(confluenceId)) return t;
        return { ...t, confluences: t.confluences.filter(c => c !== confluenceId) };
      }));
    }

    // Clean up setupDetails.recommendedConfluences
    setSetupDetails(prev => {
      const updated = { ...prev };
      let changed = false;
      for (const setupName of Object.keys(updated)) {
        const recs = updated[setupName]?.recommendedConfluences;
        if (recs && recs.includes(confluenceId)) {
          updated[setupName] = {
            ...updated[setupName],
            recommendedConfluences: recs.filter(c => c !== confluenceId)
          };
          changed = true;
        }
      }
      return changed ? updated : prev;
    });
  };

  const resetConfluenceToDefault = (confluenceId) => {
    setConfluenceOverrides(prev => {
      const next = { ...prev };
      delete next[confluenceId];
      return next;
    });
  };

  const renameConfluenceCategory = (oldCategory, newCategory) => {
    if (!newCategory.trim() || newCategory.trim() === oldCategory) return;
    const trimmed = newCategory.trim();

    // Update overrides for affected defaults
    const affectedDefaults = defaultConfluences.filter(dc => {
      const effective = confluenceOverrides[dc.id]?.category ?? dc.category;
      return effective === oldCategory && !confluenceOverrides[dc.id]?.deleted;
    });
    if (affectedDefaults.length > 0) {
      setConfluenceOverrides(prev => {
        const next = { ...prev };
        affectedDefaults.forEach(dc => {
          next[dc.id] = { ...(next[dc.id] || {}), category: trimmed };
        });
        return next;
      });
    }

    // Update custom confluences in the category
    setCustomConfluences(prev => prev.map(c =>
      c.category === oldCategory ? { ...c, category: trimmed } : c
    ));
  };

  const handleBulkCopyConfluences = (sourceConfluences, targetTradeIds, mode, sourceCounterConfluences = []) => {
    if ((!sourceConfluences.length && !sourceCounterConfluences.length) || !targetTradeIds.length) return;
    setTrades(prev => prev.map(t => {
      if (!targetTradeIds.includes(t.id)) return t;
      if (mode === 'replace') {
        return {
          ...t,
          confluences: [...sourceConfluences],
          counterConfluences: [...sourceCounterConfluences]
        };
      }
      // mode === 'add': merge via Set
      const mergedConf = [...new Set([...(t.confluences || []), ...sourceConfluences])];
      const mergedCounter = [...new Set([...(t.counterConfluences || []), ...sourceCounterConfluences])];
      // Enforce mutual exclusivity after merge
      const finalConf = mergedConf.filter(c => !mergedCounter.includes(c));
      const finalCounter = mergedCounter.filter(c => !mergedConf.includes(c));
      // Source wins: items in source take their source classification
      sourceConfluences.forEach(c => {
        if (!finalConf.includes(c)) finalConf.push(c);
        const idx = finalCounter.indexOf(c);
        if (idx !== -1) finalCounter.splice(idx, 1);
      });
      sourceCounterConfluences.forEach(c => {
        if (!finalCounter.includes(c)) finalCounter.push(c);
        const idx = finalConf.indexOf(c);
        if (idx !== -1) finalConf.splice(idx, 1);
      });
      return { ...t, confluences: finalConf, counterConfluences: finalCounter };
    }));
  };

  return {
    // Editing state
    editingConfluence,
    setEditingConfluence,
    editConfluenceName,
    setEditConfluenceName,
    editConfluenceCategory,
    setEditConfluenceCategory,
    // Functions
    startEditingConfluence,
    saveEditedConfluence,
    cancelEditingConfluence,
    deleteConfluence,
    resetConfluenceToDefault,
    renameConfluenceCategory,
    handleBulkCopyConfluences
  };
};

export default useConfluenceManagement;
